import { listed, percent, plural } from './format.js';
import { age, bytes, eventsAbout, hasItems, hoursBetween, insideOf, kindOf, quantity, } from './inside.js';
import { isEvaluation } from './model.js';
import { THRESHOLDS } from './thresholds.js';
/**
 * Whether anyone would hear about a problem (IM-206): alerts that reach a
 * receiver, and a Prometheus that stays up long enough to raise them.
 */
const W = THRESHOLDS.workloads;
/**
 * Where the Alertmanager sends alerts (IM-206).
 *
 * What can be read decides what can be said. An AlertmanagerConfig is a
 * Kubernetes object, and its receivers' names and kinds are kept (never their
 * URLs). The Alertmanager's own base configuration is a Secret, which the
 * diagnostics role is never allowed to read — and as the installer writes it,
 * that configuration sends every alert to a receiver named `null`. One
 * production instance ran five months paging nobody that way. So no
 * AlertmanagerConfig with somewhere to send is a warning that says exactly
 * that, rather than a failure claimed about a Secret nothing here has seen;
 * an AlertmanagerConfig whose receivers are all empty is a failure, because
 * that much is read.
 */
export const alertRoutingCheck = {
    id: 'alert-routing',
    title: 'Alerts reach a receiver',
    category: 'observability',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const managers = kindOf(inside.value, 'alertmanagers', 'the Alertmanagers');
        if (!hasItems(managers))
            return managers;
        if (managers.items.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'This cluster runs no Alertmanager.',
                evidence: [],
            };
        }
        const configsRead = kindOf(inside.value, 'alertmanagerConfigs', 'the AlertmanagerConfigs');
        if (!hasItems(configsRead) && configsRead.outcome === 'unknown')
            return configsRead;
        const configs = hasItems(configsRead) ? configsRead.items : [];
        const sending = configs.filter((c) => c.receivers.some((r) => r.types.length > 0));
        const evidence = [
            ...configs.map((c) => ({
                label: `AlertmanagerConfig ${c.name}`,
                value: c.receivers.length === 0
                    ? 'no receivers'
                    : listed(c.receivers.map((r) => `${r.name}${r.types.length === 0 ? ' (sends nowhere)' : ` → ${listed(r.types)}`}`)),
            })),
            ...managers.items.flatMap((m) => m.globalConfig === undefined
                ? []
                : [{ label: `Alertmanager ${m.name}`, value: `configured by ${m.globalConfig}` }]),
        ];
        if (sending.length > 0) {
            return {
                outcome: 'ok',
                finding: `${plural(sending.length, 'AlertmanagerConfig')} ${sending.length === 1 ? 'sends' : 'send'} alerts to a receiver.`,
                evidence,
            };
        }
        if (configs.length > 0) {
            return {
                outcome: 'fail',
                finding: 'Every receiver the AlertmanagerConfigs define sends nowhere, so no alert reaches anyone.',
                recommendation: 'Give the receiver a destination — an email, SNS topic or chat webhook — and send a test alert through it.',
                evidence,
            };
        }
        return {
            outcome: 'warn',
            finding: 'No AlertmanagerConfig sends alerts anywhere. The Alertmanager’s base configuration is kept in a Secret, which Qrvey does not read; as installed, it sends every alert to a receiver named null.',
            recommendation: 'Add an AlertmanagerConfig that routes alerts to the team that responds, and send a test alert through it.',
            evidence: [
                ...evidence,
                { label: 'Alertmanagers', value: listed(managers.items.map((m) => m.name)) },
            ],
        };
    },
};
/**
 * Whether Prometheus fits where it runs (IM-206).
 *
 * The kubelet evicts on node memory pressure before a pod's own limit is
 * reached, and a Prometheus evicted loses everything since its last block —
 * one lost two weeks of history five times in three days, on an 8 GiB node
 * with a 7Gi limit. The story asked for evictions over seven days; the cluster
 * keeps events for about an hour, and an evicted Prometheus pod is replaced
 * under the same name, so a week is not readable from here (IM-207 reads
 * Prometheus's own record). What is readable decides: an eviction or a kill
 * for memory in that hour fails; memory at a large share of the node's or of
 * its own limit warns before the next one.
 */
export const prometheusFitCheck = {
    id: 'prometheus-fit',
    title: 'Prometheus fits its node',
    category: 'observability',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const proms = kindOf(inside.value, 'prometheuses', 'the Prometheus resources');
        if (!hasItems(proms))
            return proms;
        if (proms.items.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'This cluster runs no Prometheus.',
                evidence: [],
            };
        }
        const podsRead = kindOf(inside.value, 'pods', 'the pods');
        if (!hasItems(podsRead)) {
            return podsRead.outcome === 'not-applicable' ? { ...podsRead, outcome: 'unknown' } : podsRead;
        }
        const nodesRead = kindOf(inside.value, 'nodes', 'the nodes');
        const nodes = hasItems(nodesRead) ? nodesRead.items : [];
        const eventsRead = kindOf(inside.value, 'events', 'the events');
        const events = hasItems(eventsRead) ? eventsRead.items : [];
        const metricsRead = kindOf(inside.value, 'podMetrics', 'pod usage');
        const metrics = hasItems(metricsRead) ? metricsRead.items : [];
        let worst = 'ok';
        const reasons = [];
        const evidence = [];
        for (const prom of proms.items) {
            const own = podsRead.items.filter((p) => p.namespace === prom.namespace && p.name.startsWith(`prometheus-${prom.name}-`));
            const evicted = eventsAbout(events, own)
                .filter((e) => e.reason === 'Evicted')
                .reduce((n, e) => n + (e.count ?? 1), 0);
            const oomKilled = own.some((p) => p.containers.some((c) => c.lastTermination?.reason === 'OOMKilled'));
            const limit = quantity(prom.limits['memory']);
            const parts = [];
            if (own.length === 0) {
                worst = worst === 'fail' ? 'fail' : 'warn';
                reasons.push(`no ${prom.name} pod is running`);
                parts.push('no pod running');
            }
            for (const pod of own) {
                const used = metrics
                    .find((m) => m.namespace === pod.namespace && m.name === pod.name)
                    ?.containers.reduce((n, c) => n + (quantity(c.memory) ?? 0), 0);
                const node = nodes.find((n) => n.name === pod.node);
                const allocatable = quantity(node?.allocatable['memory']);
                const ofNode = used !== undefined && allocatable !== undefined ? used / allocatable : undefined;
                const ofLimit = used !== undefined && limit !== undefined ? used / limit : undefined;
                const since = pod.startedAt ?? pod.createdAt;
                parts.push([
                    used === undefined ? 'memory use not reported' : `${bytes(used)} used`,
                    limit === undefined ? 'no memory limit' : `limit ${bytes(limit)}`,
                    allocatable === undefined
                        ? undefined
                        : `node ${node?.instanceType ?? pod.node ?? ''} allocates ${bytes(allocatable)}${ofNode === undefined ? '' : ` (${percent(ofNode * 100)} of it)`}`,
                    since === undefined ? undefined : `started ${age(hoursBetween(since, facts.at))} ago`,
                ]
                    .filter((part) => part !== undefined)
                    .join(', '));
                if ((ofNode ?? 0) >= W.prometheusMemoryWarnRatio ||
                    (ofLimit ?? 0) >= W.prometheusMemoryWarnRatio) {
                    if (worst === 'ok')
                        worst = 'warn';
                    reasons.push(`${prom.name} uses most of the memory it can have`);
                }
            }
            if (evicted > 0) {
                worst = 'fail';
                reasons.push(`${prom.name} was evicted ${plural(evicted, 'time')} in the last hour`);
                parts.push(`evicted ${plural(evicted, 'time')} in the cluster’s last hour of events`);
            }
            if (oomKilled) {
                worst = 'fail';
                reasons.push(`${prom.name} was killed for memory`);
                parts.push('last stopped: out of memory');
            }
            evidence.push({ label: `Prometheus ${prom.name}`, value: parts.join(' — ') });
        }
        if (worst === 'ok') {
            return {
                outcome: 'ok',
                finding: 'Prometheus runs with room on its node and under its limit.',
                evidence,
            };
        }
        return {
            outcome: worst,
            finding: `${reasons[0] ?? 'Prometheus is short of memory'}${reasons.length > 1 ? `; ${reasons.slice(1).join('; ')}` : ''}.`.replace(/^./, (c) => c.toUpperCase()),
            recommendation: 'Size Prometheus by how many series it holds: give it a node with room above its limit, or cut what it scrapes. A larger request on the same node only moves the eviction.',
            evidence,
        };
    },
};
//# sourceMappingURL=observability.js.map